import { PathResolveService } from 'src/app/services/path-resolve.service';
import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isDatasetLockedError } from 'src/app/util/dataset-lock-util';
import { TranslocoService } from '@jsverse/transloco';
import { SnackbarService } from './snackbar.service';
import { AuthService } from './auth-service';

const SESSION_END_REASON_HEADER = 'X-Session-End-Reason';

const SESSION_END_MESSAGE_KEYS: Record<string, string> = {
  passwordChanged: 'messages.sessionEndedPasswordChanged',
  accountRemoved: 'messages.sessionEndedAccountRemoved',
};
const DEFAULT_SESSION_END_MESSAGE_KEY = 'messages.sessionEnded';

const getErrorTitle = (error: HttpErrorResponse) =>
  error.status + ' - ' + error.statusText;

const getErrorMessage =
  (error: HttpErrorResponse) =>
    ((error.error instanceof ErrorEvent) ? error.error : error) ?? error.toString();

const getErrorLocation = (error: ErrorEvent) =>
  'at ' + error.error.filename + ' (line: ' + error.error.lineno + ')';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  private isHandlingSessionEnd = false;

  constructor(
    private router: Router,
    private injector: Injector) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const returnUrl = this.router.routerState.snapshot.url;
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && error.headers.has(SESSION_END_REASON_HEADER)) {
          this.handleSessionEnded(error.headers.get(SESSION_END_REASON_HEADER));
          return EMPTY;
        }
        if (error instanceof HttpErrorResponse && !isDatasetLockedError(error) && !request.headers.get('stayOnPageIfError')) {
          if (error.error instanceof ErrorEvent) {
            // Client error
            this.router.navigate([PathResolveService.getErrorPath()],
              {
                queryParams: {
                  title: getErrorTitle(error), message: getErrorMessage(error),
                  location: getErrorLocation(error.error), returnUrl
                }
              });
            return new Observable<never>();
          } else {
            // Server error
            this.router.navigate([PathResolveService.getErrorPath()],
              { queryParams: { statusCode: error.status, returnUrl } });
            return new Observable<never>();
          }
        }
        return throwError(() => error); // use global error handler
      })
    );
  }

  private handleSessionEnded(reason: string | null): void {
    if (this.isHandlingSessionEnd) return;
    this.isHandlingSessionEnd = true;
    this.injector.get(AuthService).clearSession();

    const messageKey = SESSION_END_MESSAGE_KEYS[reason ?? ''] ?? DEFAULT_SESSION_END_MESSAGE_KEY;
    const message = this.injector.get(TranslocoService).translate(messageKey);
    this.injector.get(SnackbarService).showInfoWithCloseButton(message);
    this.router.navigate([PathResolveService.getLoginPath()]).finally(() => (this.isHandlingSessionEnd = false));
  }
}
